import { Order } from '../../services/exports/Interfaces';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';
import Tooltip from '../Tooltip';
import InfoIcon from '../../assets/logo/InfoIcon';
import useTheme from '../../hooks/ui/useTheme';
import { ReactElement, useMemo } from 'react';
import CustomLoadingIndicator from '../loading/CustomLoadingIndicator';
import PaymentMethodBrandIcons from '../payment/payment-methods/PaymentMethodBrandIcons';
import useOffersTranslations from '../../features/offers/hooks/useOffersTranslations';

interface Props {
  order: Order;
  loading?: boolean;
  className?: string;
  withSeparatedTotal?: boolean;
  withPaymentMethod?: boolean;
  withEarnedPoints?: boolean;
}

export default function OrderPaymentBreakdown({
  order,
  loading,
  className,
  withSeparatedTotal,
  withPaymentMethod,
  withEarnedPoints,
}: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:OrderPaymentBreakdown' });

  const theme = useTheme();
  const { formatCurrency } = useAppMethods();
  const earnedPoints = useMemo(() => {
    if (!withEarnedPoints) {
      return;
    }

    return order?.cashback_account_operations?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.value,
      0,
    );
  }, [withEarnedPoints, order?.cashback_account_operations]);

  const Row = ({
    name,
    amount,
    content,
    className,
  }: {
    name: any;
    amount?: number;
    content?: ReactElement;
    className?: string;
  }) => (
    <div className={classnames('flex justify-between gap-lg', className)}>
      <span className="flex items-center">{name}</span>
      <span className="">{content ?? formatCurrency(amount)}</span>
    </div>
  );

  return (
    <div className={classnames('text-sm', className)}>
      <Row name={t('subtotal')} amount={order.sub_total} />
      {order.tip > 0 && <Row name={t('tip')} amount={order.tip} className="mt-xs" />}
      {order.method_fee > 0 && <Row name={t('method_fee')} amount={order.method_fee} className="mt-xs" />}
      {order.service_fee > 0 && (
        <Row
          name={
            <>
              <Tooltip title={t(`service_fee.description`)}>
                <span className="flex items-center gap-xs">
                  {t('service_fee.title')} <InfoIcon color={theme.color.gray['300']} className="w-[18px] h-[18px]" />
                </span>
              </Tooltip>
            </>
          }
          amount={order.service_fee}
          className="mt-xs relative"
        />
      )}
      {order.payment_method_upcharge_fee > 0 && (
        <Row
          name={
            <>
              <Tooltip title={t(`payment_method_upcharge_fee.description`)}>
                <span className="flex items-center gap-xs">
                  {t('payment_method_upcharge_fee.title')}{' '}
                  <InfoIcon color={theme.color.gray['300']} className="w-[18px] h-[18px]" />
                </span>
              </Tooltip>
            </>
          }
          amount={order.payment_method_upcharge_fee}
          className="mt-xs relative"
        />
      )}
      {order.additional_tax > 0 && <Row name={t('tax')} amount={order.additional_tax} className="mt-xs" />}
      <div
        className={classnames(
          'flex justify-between gap-lg mt-xs items-start',
          withSeparatedTotal && 'border-dashed border-t border-t-mobile-secondary pt-md',
        )}
      >
        <span className="flex items-center">{t('total')}</span>
        <span>
          <span className={classnames('flex items-center space-x-md place-content-end', { hidden: loading })}>
            {withPaymentMethod && (
              <PaymentMethodBrandIcons
                payment_method_id={order.payment.payment_method_id}
                payment_method_brand={order.payment.payment_method_brand}
              />
            )}
            <span>{formatCurrency(order.total)}</span>
          </span>
          {!!earnedPoints && (
            <span className="place-content-end flex text-brand-primary font-medium text-sm">
              {t('earned_points', { points: earnedPoints })}
            </span>
          )}
          <CustomLoadingIndicator className={classnames('w-12', { hidden: !loading })} />
        </span>
      </div>
      {order.total_discount > 0 && (
        <>
          <hr className="my-md" />
          <Row name={t('discount')} amount={-order.total_discount} className="text-brand-text-secondary" />
        </>
      )}
      <AppliedOffersRow order={order} />
      <AppliedCouponRow order={order} />
    </div>
  );
}

type AppliedRowProps = {
  order: Order;
};

const AppliedOffersRow = ({ order }: AppliedRowProps) => {
  const { getLabelByOffer } = useOffersTranslations();
  const appliedOffers = order.applied_offers;

  if (!appliedOffers?.length) {
    return;
  }

  return (
    <>
      {appliedOffers.map((appliedOffer) => (
        <p className="mt-xs flex justify-between items-center text-brand-text-secondary text-sm" key={appliedOffer.id}>
          {getLabelByOffer(appliedOffer)}
        </p>
      ))}
    </>
  );
};

const AppliedCouponRow = ({ order }: AppliedRowProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Shared:Coupons' });

  if (!order.coupon_type) {
    return;
  }

  return (
    <p className="mt-xs text-brand-text-secondary text-sm">
      {t(order.coupon_type, { code: order.coupon_details?.code })}
    </p>
  );
};
